import { useEffect, useState } from 'react'
import { Authenticated, Unauthenticated, AuthLoading } from 'convex/react'
import Header from './components/Header'
import Toolbar from './components/Toolbar'
import Sidebar from './components/Sidebar'
import Canvas from './components/Canvas'
import PropertiesPanel from './components/PropertiesPanel'
import AIAssistantPanel from './components/AIAssistantPanel'
import DescriptionView from './components/DescriptionView'
import DependencyView from './components/DependencyView'
import Welcome from './components/Welcome'
import MobileTabBar from './components/MobileTabBar'
import BottomSheet from './components/BottomSheet'
import StatusBanners from './components/StatusBanners'
import { ProjectProvider, useProject } from './stores/ProjectContext'
import { ThemeProvider } from './stores/ThemeContext'
import { I18nProvider } from './stores/I18nContext'
import { AuthProvider } from './stores/AuthContext'
import { CollaborationProvider, useCollaboration } from './stores/CollaborationContext'
import { AIProvider } from './stores/AIContext'
import { useIsMobile } from './hooks/useIsMobile'
import { useIsOffline } from './hooks/useIsOffline'

// Render the correct main view based on activeView.
// On mobile, the PropertiesPanel is moved out and presented as a bottom sheet.
function MainContent({ isMobile }) {
  const { activeView } = useProject()

  if (activeView === 'description') {
    return <DescriptionView />
  }

  if (activeView === 'dependencies') {
    return <DependencyView />
  }

  if (isMobile) {
    // Sidebar becomes a drawer; PropertiesPanel becomes a bottom sheet.
    // Both are handled at the App level so the canvas takes the full width.
    return <Canvas />
  }

  return (
    <>
      <Sidebar />
      <Canvas />
      <PropertiesPanel />
    </>
  )
}

// Mobile-only: PropertiesPanel inside a BottomSheet that opens when a flow is selected.
function MobilePropertiesSheet() {
  const { selectedFlow, project, setSelectedFlow, setSelectedCheckpoint } = useProject()
  const open = !!selectedFlow
  const flow = project.flows.find((f) => f.id === selectedFlow)
  const close = () => {
    setSelectedFlow(null)
    setSelectedCheckpoint(null)
  }
  return (
    <BottomSheet open={open} onClose={close} title={flow?.name || ''}>
      <PropertiesPanel />
    </BottomSheet>
  )
}

// Mobile-only: Sidebar (flow list) inside a left drawer.
// Opened via a "Flows" affordance in the header or canvas; here we listen to
// a custom event so any component can trigger it.
function MobileFlowsDrawer() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const onOpen = () => setOpen(true)
    const onClose = () => setOpen(false)
    window.addEventListener('teddo:open-flows-drawer', onOpen)
    window.addEventListener('teddo:close-flows-drawer', onClose)
    return () => {
      window.removeEventListener('teddo:open-flows-drawer', onOpen)
      window.removeEventListener('teddo:close-flows-drawer', onClose)
    }
  }, [])
  if (!open) return null
  return (
    <div
      className="mobile-drawer-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false)
      }}
    >
      <div className="mobile-drawer mobile-drawer-left">
        <Sidebar />
      </div>
    </div>
  )
}

// Component to manage presence tracking based on currentProjectId
function PresenceManager() {
  const { currentProjectId } = useProject()
  const { startPresence, stopPresence } = useCollaboration()

  useEffect(() => {
    if (currentProjectId) {
      startPresence(currentProjectId)
      return () => {
        stopPresence(currentProjectId)
      }
    }
  }, [currentProjectId]) // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally omit startPresence/stopPresence from deps — including them
  // caused a cascade: startPresence sets CollaborationContext state →
  // stopPresence gets a new reference → effect re-fires → new interval → loop.
  // Both functions are stable (useCallback with stable deps) so this is safe.

  return null
}

function AppContent() {
  const isMobile = useIsMobile()
  const isOffline = useIsOffline()
  return (
    <>
      <AuthLoading>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--bg-primary)',
          color: 'var(--text-primary)'
        }}>
          <div>Loading...</div>
        </div>
      </AuthLoading>

      <Unauthenticated>
        <Welcome />
      </Unauthenticated>

      <Authenticated>
        <AuthProvider>
          <ProjectProvider>
            <CollaborationProvider>
              <AIProvider>
                <div className={`app ${isMobile ? 'is-mobile has-mobile-tabbar' : ''} ${isOffline ? 'is-offline' : ''}`}>
                  <PresenceManager />
                  <StatusBanners />
                  <Header />
                  <Toolbar />
                  <div className="app-workspace">
                    <div className="main-content">
                      <MainContent isMobile={isMobile} />
                    </div>
                    <AIAssistantPanel />
                  </div>
                  {isMobile && (
                    <>
                      <MobileFlowsDrawer />
                      <MobilePropertiesSheet />
                      <MobileTabBar />
                    </>
                  )}
                </div>
              </AIProvider>
            </CollaborationProvider>
          </ProjectProvider>
        </AuthProvider>
      </Authenticated>
    </>
  )
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AppContent />
      </I18nProvider>
    </ThemeProvider>
  )
}

export default App
